/**
 * Header control inside `SubDelegatesModal`: seat-usage pill + pencil
 * trigger that opens `EditSeatLimitDialog`. Owns the update-limit
 * mutation so the modal stays within the 300-line budget.
 */

import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { SeatUsagePill } from '@/shared/components/sub-delegation';
import { useToast } from '@/shared/components/Toast';
import { useUpdateAdminSubDelegateLimit } from '../../hooks/useAdminSubDelegates';
import { EditSeatLimitDialog } from './EditSeatLimitDialog';

const DEFAULT_SEAT_LIMIT = 3;

type SeatLimitControlProps = {
  ownerId: string;
  ownerName: string;
  /** Raw `users.sub_delegate_limit` (`null` = default applies). */
  limit: number | null;
  effectiveLimit: number;
  usedCount: number;
  errorMessageFrom: (error: unknown) => string | null;
};

export function SeatLimitControl({
  ownerId,
  ownerName,
  limit,
  effectiveLimit,
  usedCount,
  errorMessageFrom,
}: SeatLimitControlProps) {
  const { showToast } = useToast();
  const updateLimit = useUpdateAdminSubDelegateLimit(ownerId);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSave = async (newLimit: number | null) => {
    setSaveError(null);
    try {
      await updateLimit.mutateAsync(newLimit);
      showToast(
        newLimit === null
          ? 'Seat limit reset to default'
          : 'Seat limit updated',
        'success',
      );
      setIsDialogOpen(false);
    } catch (error) {
      setSaveError(errorMessageFrom(error));
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <SeatUsagePill
        used={usedCount}
        limit={effectiveLimit}
        isDefaultLimit={limit === null}
      />
      <button
        type="button"
        aria-label="Edit seat limit"
        title="Edit seat limit"
        onClick={() => setIsDialogOpen(true)}
        className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-[#026857]"
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>

      <EditSeatLimitDialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) setSaveError(null);
        }}
        ownerName={ownerName}
        currentLimit={limit}
        defaultLimit={DEFAULT_SEAT_LIMIT}
        usedCount={usedCount}
        isSaving={updateLimit.isPending}
        errorMessage={saveError}
        onSave={handleSave}
      />
    </div>
  );
}
